import { Box, Flex, font, preventOneWordInLastLine } from '@lemonade-hq/cdk';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import React from 'react';
import styled, { type Keyframes, keyframes, css } from 'styled-components';

export const PARAGRAPH_WIDTH = '32em';
export const END_DURATION: Milliseconds = 4500;
const PARAGRAPH_DURATION: Milliseconds = 3000;
const PARAGRAPH_FADE_DURATION_RATIO = 0.1;
const PARAGRAPH_SINGLE_FADE_DURATION: Milliseconds = PARAGRAPH_DURATION * PARAGRAPH_FADE_DURATION_RATIO;
export const TOTAL_PARAGRAPH_DURATION: Milliseconds = PARAGRAPH_DURATION * (1 + 2 * PARAGRAPH_FADE_DURATION_RATIO);

// TODO: make the keyframe interpolation thingy a helper in CDK because it's super useful
const crossFade = (outOf: number): Keyframes => {
  const new100 = 100 * ((TOTAL_PARAGRAPH_DURATION * outOf) / (TOTAL_PARAGRAPH_DURATION * outOf + END_DURATION));
  return keyframes`
        0% {
            opacity: 0;
        }

        ${(new100 / outOf) * PARAGRAPH_FADE_DURATION_RATIO}% {
            opacity: 1;
        }

        ${(new100 / outOf) * (1 - PARAGRAPH_FADE_DURATION_RATIO)}% {
            opacity: 1;
        }

        ${new100 / outOf}% {
            opacity: 0;
        }

        100% {
            opacity: 0;
        }
    `;
};

export const ParagraphContainer = styled(Flex)<{
  outOf: number;
  index: number;
}>`
  text-align: center;
  width: ${PARAGRAPH_WIDTH};
  flex-direction: column;
  justify-content: space-between;
  user-select: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  ${({ outOf, index }) => css`
    animation-name: ${crossFade(outOf)};
    animation-duration: ${outOf * TOTAL_PARAGRAPH_DURATION + END_DURATION}ms;
    animation-delay: ${index * TOTAL_PARAGRAPH_DURATION - PARAGRAPH_SINGLE_FADE_DURATION}ms;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    animation-fill-mode: both;
  `};
`;

const Title = styled.h4`
  ${font('main', { fontSize: '3.0em', fontWeight: 'bold', lineHeight: 1.1 })};
  white-space: pre-line;
  margin-bottom: 0.6em;
`;

const Organization = styled.p`
  ${font('main', { fontSize: '1.6em', fontWeight: 'normal' })};
`;

export interface Giveback2021BaseParagraphProps {
  title: string;
  subtitle: string;
  organization: string;
  amount: string;
}

export interface Giveback2021ParagraphProps
  extends Omit<Giveback2021BaseParagraphProps, 'amount'>,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  titleColor: string;
  orgColor: string;
  outOf: number;
  index: number;
  paused: boolean;
  mt?: number;
  mb?: number;
}

const Giveback2021Paragraph: React.FC<Giveback2021ParagraphProps> = ({
  title,
  subtitle,
  organization,
  titleColor,
  orgColor,
  outOf,
  index,
  paused,
  mb,
  mt,
  ...props
}) => {
  return (
    <ParagraphContainer
      style={{ animationPlayState: paused ? 'paused' : 'running' }}
      mb={mb ?? 0}
      mt={mt ?? 0}
      outOf={outOf}
      index={index}
      {...props}
    >
      <Box>
        <Title
          style={{ color: titleColor }}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
          dangerouslySetInnerHTML={{
            __html: preventOneWordInLastLine(`${title} ${subtitle}`),
          }}
        />
      </Box>
      <Box style={{ color: orgColor }}>
        <Organization>
          via&nbsp;
          {organization}
        </Organization>
      </Box>
    </ParagraphContainer>
  );
};

export default React.memo(Giveback2021Paragraph);
